import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import RefreshIcon from '@mui/icons-material/Refresh';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { auditsApi } from '../services/audits';
import { tasksApi } from '../services/tasks';
import type { Audit, BrandPerceptionSummary, CompetitorAnalysis, CompetitorMentionStats } from '@traaker/shared';

const SPARKLINE_AUDIT_COUNT = 10;
const ANALYZING_STORAGE_PREFIX = 'traaker:competitors:analyzing:';

type SoVPoint = { createdAt: string; pct: number };
type SoVSeries = { key: string; label: string; isBrand: boolean; latest: number; previous?: number; points: SoVPoint[] };

export default function Competitors() {
  const { activeBrand, refreshBrands } = useBrand();
  const { tenant } = useAuth();
  const [analyzing, setAnalyzing] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState('');
  const [history, setHistory] = useState<Audit[]>([]);

  const isPro = tenant?.tier === 'pro' || tenant?.tier === 'enterprise';
  const competitors = activeBrand?.competitors || [];
  const analyses = activeBrand?.competitorAnalyses || {};
  const brandId = activeBrand?.brandId;
  const storageKey = brandId ? `${ANALYZING_STORAGE_PREFIX}${brandId}` : null;

  // Restore in-flight analysis state on mount / brand switch so a refresh
  // doesn't flip the button back to "Analyser".
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) setAnalyzing(new Set(JSON.parse(raw) as string[]));
      else setAnalyzing(new Set());
    } catch {
      setAnalyzing(new Set());
    }
  }, [storageKey]);

  const persistAnalyzing = (next: Set<string>) => {
    if (!storageKey) return;
    try {
      if (next.size === 0) sessionStorage.removeItem(storageKey);
      else sessionStorage.setItem(storageKey, JSON.stringify([...next]));
    } catch {
      // sessionStorage can throw in private mode — purely cosmetic, ignore.
    }
  };

  useEffect(() => {
    if (!isPro || !brandId) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const audits = await auditsApi.list('brand', brandId);
        if (cancelled) return;
        const completed = audits
          .filter((a) => a.status === 'completed' && !!a.brandPerceptionSummary)
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .slice(-SPARKLINE_AUDIT_COUNT);
        setHistory(completed);
      } catch {
        if (!cancelled) setHistory([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPro, brandId]);

  const latestAudit = history.length ? history[history.length - 1] : null;
  const perceptionSummary: BrandPerceptionSummary | undefined = latestAudit?.brandPerceptionSummary;
  const competitorMentions = perceptionSummary?.competitor_mentions || {};
  const totalQueries = perceptionSummary?.total_queries || 0;

  const series = useMemo(
    () => buildSovSeries(history, competitors, activeBrand?.name),
    [history, competitors, activeBrand?.name],
  );

  const leaderboard = useMemo(() => [...series].sort((a, b) => b.latest - a.latest), [series]);
  const seriesByKey = useMemo(() => {
    const map = new Map<string, SoVSeries>();
    for (const s of series) map.set(s.key, s);
    return map;
  }, [series]);

  const handleAnalyze = async (competitorName: string) => {
    if (!brandId) return;
    setAnalyzing((prev) => {
      const next = new Set(prev).add(competitorName);
      persistAnalyzing(next);
      return next;
    });
    setError('');
    try {
      await api.post(`/brands/${brandId}/competitors/analyze`, { name: competitorName });
      await refreshBrands();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing((prev) => {
        const next = new Set(prev);
        next.delete(competitorName);
        persistAnalyzing(next);
        return next;
      });
    }
  };

  const staleOrMissing = useMemo(() => {
    return competitors.filter((name) => {
      const a = analyses[name];
      if (!a) return true;
      const ageDays = Math.floor((Date.now() - new Date(a.analyzedAt).getTime()) / (24 * 60 * 60 * 1000));
      return ageDays > 7;
    });
  }, [competitors, analyses]);

  const handleAnalyzeAllStale = async () => {
    if (!brandId || staleOrMissing.length === 0) return;
    setError('');
    // Sequential to avoid slamming the API and to respect any per-tenant rate limits.
    for (const name of staleOrMissing) {
      if (analyzing.has(name)) continue;
      setAnalyzing((prev) => {
        const next = new Set(prev).add(name);
        persistAnalyzing(next);
        return next;
      });
      try {
        await api.post(`/brands/${brandId}/competitors/analyze`, { name });
      } catch (err: any) {
        setError(err.message || `Erreur lors de l'analyse de ${name}`);
      } finally {
        setAnalyzing((prev) => {
          const next = new Set(prev);
          next.delete(name);
          persistAnalyzing(next);
          return next;
        });
      }
    }
    await refreshBrands();
  };

  if (!isPro) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          Concurrents
        </Typography>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Fonctionnalite Pro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              L'analyse concurrentielle est disponible avec les plans Pro et Enterprise.
              Analysez le positionnement, les forces et faiblesses de vos concurrents
              par rapport a votre strategie de marque.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const batchInFlight = staleOrMissing.some((n) => analyzing.has(n));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Concurrents
        </Typography>
        {staleOrMissing.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={batchInFlight ? <CircularProgress size={14} color="inherit" /> : <AnalyticsIcon />}
            onClick={handleAnalyzeAllStale}
            disabled={batchInFlight}
          >
            {batchInFlight
              ? `Analyse en cours (${staleOrMissing.filter((n) => analyzing.has(n)).length}/${staleOrMissing.length})...`
              : `Analyser ${staleOrMissing.length} concurrent${staleOrMissing.length > 1 ? 's' : ''} a mettre a jour`}
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analyse comparative de vos concurrents : positionnement, forces, faiblesses,
        et presence en ligne. Les donnees sont extraites de leur site web et de leurs
        mentions publiques, puis comparees a votre strategie de marque.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {leaderboard.length > 0 && totalQueries > 0 && (
        <LeaderboardCard
          rows={leaderboard}
          totalQueries={totalQueries}
          auditDate={latestAudit?.createdAt}
          historyCount={history.length}
        />
      )}

      {competitors.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucun concurrent configure
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ajoutez des concurrents dans les parametres de votre marque
              pour lancer l'analyse comparative.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {competitors.map((name) => {
            const analysis = analyses[name];
            const isAnalyzing = analyzing.has(name);
            const mentionStats = findMentionStats(competitorMentions, name);
            const sovSeries = seriesByKey.get(canonicalKey(name));

            return (
              <Grid key={name} size={{ xs: 12 }}>
                <CompetitorCard
                  name={name}
                  analysis={analysis}
                  analyzing={isAnalyzing}
                  onAnalyze={() => handleAnalyze(name)}
                  brandName={activeBrand?.name}
                  mentionStats={mentionStats}
                  totalQueries={totalQueries}
                  auditDate={latestAudit?.createdAt}
                  sovSeries={sovSeries}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

// ─── Share-of-Voice computation ─────────────────────────────────────────────

function canonicalKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function findMentionStats(
  mentions: Record<string, CompetitorMentionStats>,
  competitorName: string,
): CompetitorMentionStats | undefined {
  const target = canonicalKey(competitorName);
  if (!target) return undefined;
  if (mentions[target]) return mentions[target];
  // Agent keys by raw lowercase; our canonical also folds diacritics + whitespace.
  // Only fall back to a canonical-equivalence match on the stored keys — never substring.
  for (const [key, stats] of Object.entries(mentions)) {
    if (canonicalKey(key) === target) return stats;
  }
  return undefined;
}

function buildSovSeries(
  history: Audit[],
  competitors: string[],
  brandName?: string,
): SoVSeries[] {
  if (history.length === 0) return [];

  const entries: { key: string; label: string; isBrand: boolean }[] = [];
  if (brandName) entries.push({ key: canonicalKey(brandName), label: brandName, isBrand: true });
  for (const c of competitors) entries.push({ key: canonicalKey(c), label: c, isBrand: false });

  return entries
    .filter((e) => e.key)
    .map((e) => {
      const points: SoVPoint[] = history.map((audit) => {
        const summary = audit.brandPerceptionSummary;
        const total = summary?.total_queries || 0;
        let pct = 0;
        if (summary && total > 0) {
          if (e.isBrand) {
            // Use the authority citation rate (same numerator/denominator shape as
            // competitor mentions — "queries where the brand's domain was cited").
            pct = Math.round((summary.referral?.overall_citation_rate || 0) * 100);
          } else {
            const stats = findMentionStats(summary.competitor_mentions || {}, e.label);
            pct = stats ? Math.round((stats.total / total) * 100) : 0;
          }
        }
        return { createdAt: audit.createdAt, pct };
      });
      const latest = points.length ? points[points.length - 1].pct : 0;
      const previous = points.length >= 2 ? points[points.length - 2].pct : undefined;
      return { key: e.key, label: e.label, isBrand: e.isBrand, latest, previous, points };
    });
}

// ─── Leaderboard ────────────────────────────────────────────────────────────

function LeaderboardCard({
  rows,
  totalQueries,
  auditDate,
  historyCount,
}: {
  rows: SoVSeries[];
  totalQueries: number;
  auditDate?: string;
  historyCount: number;
}) {
  const auditLabel = auditDate ? new Date(auditDate).toLocaleDateString('fr-FR') : null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <VisibilityIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          <Typography variant="h6" fontWeight={700}>
            Part de voix IA
          </Typography>
          {auditLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Dernier audit : {auditLabel}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          % des {totalQueries} requetes ou chaque acteur est cite par les moteurs IA.
          {historyCount >= 2 && <> Evolution sur les {historyCount} derniers audits.</>}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row, i) => (
            <LeaderboardRow key={row.key} row={row} rank={i + 1} max={rows[0]?.latest || 0} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function LeaderboardRow({ row, rank, max }: { row: SoVSeries; rank: number; max: number }) {
  const delta = row.previous !== undefined ? row.latest - row.previous : undefined;
  const barPct = max > 0 ? Math.max(2, (row.latest / max) * 100) : 0;
  const color = row.isBrand ? '#00DDB3' : '#7C3AED';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 20, textAlign: 'right' }}>
        {rank}.
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="body2"
            fontWeight={row.isBrand ? 700 : 500}
            sx={{ color: row.isBrand ? color : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {row.label}
          </Typography>
          {row.isBrand && (
            <Chip size="small" label="Vous" sx={{ height: 18, fontSize: 11, bgcolor: `${color}20`, color }} />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeltaChip delta={delta} />
            <Typography variant="body2" fontWeight={700} sx={{ minWidth: 44, textAlign: 'right' }}>
              {row.latest}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ height: 6, bgcolor: 'rgba(11,11,15,0.06)', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${barPct}%`, bgcolor: color, borderRadius: 3 }} />
        </Box>
      </Box>
      <Box sx={{ width: 80, flexShrink: 0 }}>
        <Sparkline points={row.points.map((p) => p.pct)} color={color} />
      </Box>
    </Box>
  );
}

function DeltaChip({ delta }: { delta?: number }) {
  if (delta === undefined) return null;
  if (delta === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
        <TrendingFlatIcon fontSize="small" />
      </Box>
    );
  }
  const positive = delta > 0;
  const color = positive ? '#00DDB3' : '#FF4444';
  const Icon = positive ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color }}>
      <Icon fontSize="small" />
      <Typography variant="caption" fontWeight={600}>
        {positive ? '+' : ''}{delta}pt
      </Typography>
    </Box>
  );
}

function Sparkline({ points, color }: { points: number[]; color: string }) {
  if (points.length < 2) {
    return <Box sx={{ height: 20 }} />;
  }
  const w = 80;
  const h = 20;
  const max = Math.max(...points, 1);
  const stepX = w / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = i * stepX;
      const y = h - (p / max) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── Competitor card ────────────────────────────────────────────────────────

function CompetitorCard({
  name,
  analysis,
  analyzing,
  onAnalyze,
  brandName,
  mentionStats,
  totalQueries,
  auditDate,
  sovSeries,
}: {
  name: string;
  analysis?: CompetitorAnalysis;
  analyzing: boolean;
  onAnalyze: () => void;
  brandName?: string;
  mentionStats?: CompetitorMentionStats;
  totalQueries?: number;
  auditDate?: string;
  sovSeries?: SoVSeries;
}) {
  if (!analysis) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Pas encore analyse
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <AnalyticsIcon />}
              onClick={onAnalyze}
              disabled={analyzing}
            >
              {analyzing ? 'Analyse en cours...' : 'Analyser'}
            </Button>
          </Box>
          {mentionStats && totalQueries ? (
            <>
              <Divider sx={{ my: 2 }} />
              <VisibilityBlock
                mentionStats={mentionStats}
                totalQueries={totalQueries}
                auditDate={auditDate}
                brandName={brandName}
                sovSeries={sovSeries}
              />
            </>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const age = Date.now() - new Date(analysis.analyzedAt).getTime();
  const ageDays = Math.floor(age / (24 * 60 * 60 * 1000));
  const isStale = ageDays > 7;

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>{name}</Typography>
            {analysis.url && (
              <Tooltip title="Voir le site">
                <IconButton size="small" onClick={() => window.open(analysis.url!.startsWith('http') ? analysis.url : `https://${analysis.url}`, '_blank')}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {analysis.strategy?.industry && (
              <Chip label={analysis.strategy.industry} size="small" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {isStale ? 'Obsolete' : `Analyse du ${new Date(analysis.analyzedAt).toLocaleDateString('fr-FR')}`}
            </Typography>
            <Tooltip title="Re-analyser">
              <IconButton size="small" onClick={onAnalyze} disabled={analyzing}>
                {analyzing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Visibilite IA (from latest brand perception audit) */}
        {mentionStats && totalQueries ? (
          <Box sx={{ mb: 2.5 }}>
            <VisibilityBlock
              mentionStats={mentionStats}
              totalQueries={totalQueries}
              auditDate={auditDate}
              brandName={brandName}
              sovSeries={sovSeries}
            />
          </Box>
        ) : null}

        {/* Positioning */}
        {analysis.comparison.positioning && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
              {analysis.comparison.positioning}
            </Typography>
          </Box>
        )}

        {/* Strategy summary */}
        {analysis.strategy?.description && (
          <Box sx={{ mb: 2.5, bgcolor: 'rgba(11,11,15,0.03)', borderRadius: 1, p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Positionnement du concurrent
            </Typography>
            <Typography variant="body2">{analysis.strategy.description}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <GapBlock
          competitorName={name}
          brandName={brandName}
          comparison={analysis.comparison}
        />

        {analysis.comparison.sharedTopics.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <CompareArrowsIcon fontSize="small" sx={{ color: '#7C3AED' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Sujets en commun
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {analysis.comparison.sharedTopics.map((t, i) => (
                <Chip key={i} label={t} size="small" variant="outlined" />
              ))}
            </Box>
          </>
        )}

        {/* Earned media signals */}
        {analysis.earnedMediaSignals.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Mentions publiques ({analysis.earnedMediaSignals.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {analysis.earnedMediaSignals.slice(0, 5).map((signal, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(11,11,15,0.03)',
                    borderRadius: 1,
                    p: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(11,11,15,0.06)' },
                  }}
                  onClick={() => window.open(signal.sourceUrl, '_blank')}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {signal.sourceName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {signal.snippet}
                  </Typography>
                  <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function VisibilityBlock({
  mentionStats,
  totalQueries,
  auditDate,
  brandName,
  sovSeries,
}: {
  mentionStats: CompetitorMentionStats;
  totalQueries: number;
  auditDate?: string;
  brandName?: string;
  sovSeries?: SoVSeries;
}) {
  const pct = totalQueries > 0 ? Math.round((mentionStats.total / totalQueries) * 100) : 0;
  const auditLabel = auditDate ? new Date(auditDate).toLocaleDateString('fr-FR') : null;
  const byAxis = mentionStats.by_axis;
  const delta = sovSeries?.previous !== undefined ? sovSeries.latest - sovSeries.previous : undefined;

  return (
    <Box sx={{ bgcolor: 'rgba(124,58,237,0.06)', borderRadius: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <VisibilityIcon fontSize="small" sx={{ color: '#7C3AED' }} />
        <Typography variant="subtitle2" sx={{ color: '#7C3AED', fontWeight: 600 }}>
          Visibilite IA
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeltaChip delta={delta} />
          {auditLabel && (
            <Typography variant="caption" color="text.secondary">
              Audit du {auditLabel}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Cite dans <b>{mentionStats.total}/{totalQueries}</b> requetes ({pct}%)
        {brandName && mentionStats.cited_with_brand !== undefined && mentionStats.cited_with_brand > 0 ? (
          <> — dont <b>{mentionStats.cited_with_brand}</b> aux cotes de {brandName}</>
        ) : null}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {byAxis.narrative > 0 && (
          <Chip size="small" label={`Narratif: ${byAxis.narrative}`} sx={{ bgcolor: 'rgba(124,58,237,0.10)', color: '#7C3AED' }} />
        )}
        {byAxis.authority > 0 && (
          <Chip size="small" label={`Autorite: ${byAxis.authority}`} sx={{ bgcolor: 'rgba(124,58,237,0.10)', color: '#7C3AED' }} />
        )}
        {byAxis.referral > 0 && (
          <Chip size="small" label={`Referral: ${byAxis.referral}`} sx={{ bgcolor: 'rgba(124,58,237,0.10)', color: '#7C3AED' }} />
        )}
      </Box>
    </Box>
  );
}

// ─── Gap block: defend (your edge) vs attack (gaps to close) ────────────────

function GapBlock({
  competitorName,
  brandName,
  comparison,
}: {
  competitorName: string;
  brandName?: string;
  comparison: { strengths: string[]; weaknesses: string[]; uniqueToCompetitor: string[] };
}) {
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;
  const brandId = activeBrand?.brandId;
  const displayBrand = brandName || 'vous';

  // Semantics from the existing CompetitorComparison shape:
  //   strengths           = where the competitor is stronger  → gap to close
  //   uniqueToCompetitor  = topics they own that you don't    → gap to close
  //   weaknesses          = where you are stronger            → edge to defend
  const attack: { label: string; kind: 'strength' | 'unique' }[] = [
    ...comparison.strengths.map((s) => ({ label: s, kind: 'strength' as const })),
    ...comparison.uniqueToCompetitor.map((t) => ({ label: t, kind: 'unique' as const })),
  ];
  const defend = comparison.weaknesses;

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <GpsFixedIcon fontSize="small" sx={{ color: '#FF6B35' }} />
          <Typography variant="subtitle2" sx={{ color: '#FF6B35', fontWeight: 600 }}>
            A attaquer — ecarts a combler
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Ou {competitorName} devance {displayBrand} ou couvre des sujets que vous n'adressez pas.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {attack.length > 0 ? (
            attack.map((a, i) => (
              <AddTaskChip
                key={`a-${i}`}
                label={a.label}
                color="#FF6B35"
                enabled={tasksEnabled}
                taskTitle={
                  a.kind === 'strength'
                    ? `Combler l'ecart vs ${competitorName} : ${a.label}`
                    : `Couvrir le sujet "${a.label}" (propre a ${competitorName})`
                }
                taskDescription={
                  a.kind === 'strength'
                    ? `${competitorName} est percu comme plus fort sur "${a.label}". Produire du contenu / des preuves pour combler l'ecart.`
                    : `${competitorName} occupe le sujet "${a.label}" seul. Evaluer si ${displayBrand} doit l'investir et produire du contenu si oui.`
                }
                category="content"
                brandId={brandId}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">Aucun ecart identifie</Typography>
          )}
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <ShieldIcon fontSize="small" sx={{ color: '#00DDB3' }} />
          <Typography variant="subtitle2" sx={{ color: '#00DDB3', fontWeight: 600 }}>
            A defendre — votre avantage
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Ou {displayBrand} est percu comme plus fort. Verrouiller cet avantage avec du contenu.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {defend.length > 0 ? (
            defend.map((w, i) => (
              <AddTaskChip
                key={`d-${i}`}
                label={w}
                color="#00DDB3"
                enabled={tasksEnabled}
                taskTitle={`Defendre l'avantage : ${w}`}
                taskDescription={`${displayBrand} est percu comme plus fort que ${competitorName} sur "${w}". Produire du contenu qui verrouille cet avantage aupres des moteurs IA.`}
                category="content"
                brandId={brandId}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">Aucun avantage identifie</Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

function AddTaskChip({
  label,
  color,
  enabled,
  taskTitle,
  taskDescription,
  category,
  brandId,
}: {
  label: string;
  color: string;
  enabled: boolean;
  taskTitle: string;
  taskDescription: string;
  category: 'content' | 'external';
  brandId?: string;
}) {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const handleAdd = async () => {
    if (!enabled || added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: taskTitle.slice(0, 120),
        description: taskDescription,
        category,
        sourceType: 'manual',
        brandId,
      });
      setAdded(true);
    } catch {
      // swallow — chip-level failure should not kill the page
    } finally {
      setAdding(false);
    }
  };

  if (!enabled) {
    return <Chip label={label} size="small" sx={{ bgcolor: `${color}20`, color }} />;
  }

  const icon = adding ? (
    <CircularProgress size={12} sx={{ color }} />
  ) : added ? (
    <CheckCircleOutlineIcon sx={{ fontSize: 14, color }} />
  ) : (
    <AddTaskIcon sx={{ fontSize: 14, color }} />
  );

  return (
    <Tooltip title={added ? 'Tache creee' : 'Creer une tache'}>
      <Chip
        label={label}
        size="small"
        onClick={handleAdd}
        onDelete={handleAdd}
        deleteIcon={icon}
        disabled={adding}
        sx={{
          bgcolor: `${color}20`,
          color,
          cursor: adding ? 'default' : 'pointer',
          '& .MuiChip-deleteIcon': { color, '&:hover': { color } },
        }}
      />
    </Tooltip>
  );
}
